import { useState } from "react";
import { useNavigate } from "react-router";
import { CalendarPlus, UserPlus } from "lucide-react";
import { usePermissao } from "@/lib/use-permissao";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";
import { useNovaConsulta } from "@/components/agenda/use-nova-consulta";
import { useFluxoNovoPedido } from "@/components/prosthesis/FluxoNovoPedido";

export type AcaoDeCriar = {
  chave: string;
  rotulo: string;
  icone: React.ComponentType<{ className?: string }>;
  /** Cor sólida do círculo (classes Tailwind de fundo). */
  cor: string;
  executar: () => void;
  desabilitada?: boolean;
};

/** Quais ações o "+" oferece — cada uma só com a permissão de fazê-la. */
export function useTemAcaoDeCriar() {
  const agendar = usePermissao("agenda.manage");
  const cadastrar = usePermissao("patients.manage");
  const pedir = usePermissao("prosthesis.manage");
  return { agendar, cadastrar, pedir, alguma: agendar || cadastrar || pedir };
}

/**
 * As ações do "+", na ordem de uso, e os diálogos que elas abrem (`elementos`, que quem usa só
 * renderiza). Ação nova (tratamento, etc.) entra aqui.
 */
export function useAcoesDeCriar() {
  const navigate = useNavigate();
  const pode = useTemAcaoDeCriar();
  const consulta = useNovaConsulta();
  const pedido = useFluxoNovoPedido({ onConcluido: () => navigate("/protese") });
  const [cadastroAberto, setCadastroAberto] = useState(false);

  const acoes: AcaoDeCriar[] = [];
  if (pode.agendar) {
    acoes.push({ chave: "consulta", rotulo: "Agendar consulta", icone: CalendarPlus, cor: "bg-sky-500", executar: consulta.abrir, desabilitada: !consulta.pronto });
  }
  if (pode.cadastrar) {
    acoes.push({ chave: "paciente", rotulo: "Cadastrar paciente", icone: UserPlus, cor: "bg-brand-600", executar: () => setCadastroAberto(true) });
  }
  if (pode.pedir) {
    acoes.push({ chave: "protese", rotulo: "Pedir prótese", icone: CoroaDentariaIcon, cor: "bg-fuchsia-500", executar: pedido.abrirNovo });
  }

  const elementos = (
    <>
      {consulta.elementos}
      {pedido.elementos}
      <PatientFormDialog
        open={cadastroAberto}
        onClose={() => setCadastroAberto(false)}
        onCreated={(p) => {
          setCadastroAberto(false);
          navigate(`/pacientes/${p.id}`);
        }}
      />
    </>
  );

  return { acoes, elementos };
}
